import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Home as HomeIcon, Sparkles } from "lucide-react";
import { getActivePartnerships } from "../../services/partnershipsApi";
import { getCategories } from "../../services/categoriesApi";
import { getPhoto } from "../../storageService";
import Spinner from "../Spinner/Spinner";
import Breadcrumb from "../Breadcrumbs/Breadcrumbs";
import NewBadge from "../NewBadge/NewBadge";
import { useTheme } from "../../utils/ThemeContext";
import "../Providers/PartnerCard.css";
import "./PartnersPage.css";

const PartnersPage = () => {
    const { theme } = useTheme();
    const [partnerships, setPartnerships] = useState([]);
    const [categoryNameById, setCategoryNameById] = useState({});
    const [bannerUrls, setBannerUrls] = useState({});
    const [partnerPhotoUrls, setPartnerPhotoUrls] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [partnersData, catData] = await Promise.all([
                    getActivePartnerships(),
                    // getCategories devuelve objects con `id` y `name` (a diferencia de
                    // getCategoriesNameType que sólo trae name+type+label sin id).
                    // Sólo necesitamos servicios — los partnerships sólo sponsorean categorías de servicios.
                    getCategories("servicios"),
                ]);
                if (cancelled) return;

                const list = Array.isArray(partnersData) ? partnersData : [];
                setPartnerships(list);

                const nameMap = {};
                (catData || []).forEach((c) => {
                    if (c?.id && c?.name) nameMap[c.id] = c.name;
                });
                setCategoryNameById(nameMap);

                // Resolver paths de Firebase Storage → URLs descargables en paralelo
                const bannerEntries = await Promise.all(
                    list.map(async (p) => {
                        if (!p.bannerPhoto) return [p.id, null];
                        try { return [p.id, await getPhoto(p.bannerPhoto)]; } catch { return [p.id, null]; }
                    })
                );
                const photoEntries = await Promise.all(
                    list.map(async (p) => {
                        if (!p.partnerPhoto) return [p.id, null];
                        try { return [p.id, await getPhoto(p.partnerPhoto)]; } catch { return [p.id, null]; }
                    })
                );
                if (cancelled) return;
                setBannerUrls(Object.fromEntries(bannerEntries));
                setPartnerPhotoUrls(Object.fromEntries(photoEntries));
            } catch (e) {
                console.error("Error cargando partners:", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const pageTitle = "Partners | ContactaMe";
    const pageDescription =
        "Conocé a los aliados estratégicos de CONTACTAME — proveedores que ofrecen beneficios y descuentos exclusivos al resto de los proveedores de la plataforma.";
    const pageUrl = "https://contactame.com.ar/partners";

    return (
        <div className={`partners-page ${theme === "light" ? "light-theme" : "dark-theme"}`}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content="partners, alianzas, beneficios, descuentos, proveedores, contactame, argentina" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />
                <link rel="canonical" href={pageUrl} />
            </Helmet>

            <Breadcrumb items={[{ label: "Inicio", icon: <HomeIcon size={16} />, link: "/" }, { label: "Partners" }]} />

            <header className="partners-page__hero">
                <h1 className="partners-page__title">
                    Partners de <span className="brand">CONTACTAME</span>
                </h1>
                <p className="partners-page__subtitle">
                    Aliados estratégicos que ofrecen beneficios y descuentos exclusivos a los proveedores de la plataforma.
                </p>
            </header>

            {loading ? (
                <Spinner />
            ) : partnerships.length === 0 ? (
                <p className="partners-page__empty">Todavía no hay partnerships activos.</p>
            ) : (
                <div className="partners-page__grid">
                    {partnerships.map((p) => {
                        // Resolver ids → nombres; los que no resuelvan se descartan para no
                        // renderizar un UUID crudo como chip.
                        const cats = (p.sponsoredServiceCategories || [])
                            .map((id) => categoryNameById[id])
                            .filter(Boolean);
                        const banner = bannerUrls[p.id];
                        const photo = partnerPhotoUrls[p.id];
                        return (
                            <article key={p.id} className="ppage-card">
                                <NewBadge label="PARTNER" size="md" className="partner-badge" />
                                <div
                                    className={`ppage-card__banner ${banner ? "" : "ppage-card__banner--plain"}`}
                                    style={banner ? { backgroundImage: `url(${banner})` } : undefined}
                                    aria-hidden="true"
                                >
                                    {photo && (
                                        <img
                                            src={photo}
                                            alt={p.partnerName || p.productProviderId}
                                            className="ppage-card__avatar"
                                        />
                                    )}
                                </div>
                                <div className="ppage-card__body">
                                    <h2 className="ppage-card__name">{p.partnerName || p.productProviderId}</h2>
                                    {p.description && (
                                        <p className="ppage-card__description">{p.description}</p>
                                    )}
                                    {cats.length > 0 && (
                                        <div className="ppage-card__chips">
                                            <span className="ppage-card__chips-label">
                                                <Sparkles size={13} />
                                                Beneficios para
                                            </span>
                                            <ul className="ppage-card__chip-list">
                                                {cats.map((name, i) => (
                                                    <li key={i} className="ppage-card__chip">{name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <Link to={`/${p.productProviderId}`} state={{ fromBenefits: true }} className="ppage-card__cta">
                                        Ver perfil
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PartnersPage;
